import { CssBaseline, ThemeProvider } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { useMemo } from 'react';
import { Toaster } from 'sonner';
import { createAppTheme } from './theme';
import { TableThemeProvider, useTableTheme } from './tableTheme';
import { ThemePreferenceProvider, useThemePreference } from './themePreference';
import { queryClient } from './queryClient';
import { AuthProvider, useAuth } from '@/shared/auth/AuthProvider';
import { UserPreferencesProvider } from '@/shared/preferences/UserPreferencesProvider';
import { routeTree } from '../routeTree.gen';

const router = createRouter({
  routeTree,
  context: { auth: undefined! },
  defaultPreload: 'intent',
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

function RouterWithAuth() {
  const auth = useAuth();
  return <RouterProvider router={router} context={{ auth }} />;
}

function ThemedApp() {
  const { resolvedTheme } = useThemePreference();
  const { preset } = useTableTheme();
  const theme = useMemo(() => createAppTheme(resolvedTheme, preset), [preset, resolvedTheme]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <UserPreferencesProvider>
          <RouterWithAuth />
          <Toaster theme={resolvedTheme} position="top-right" richColors closeButton />
        </UserPreferencesProvider>
      </LocalizationProvider>
    </ThemeProvider>
  );
}

export function App() {
  return (
    <ThemePreferenceProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <TableThemeProvider>
            <ThemedApp />
          </TableThemeProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ThemePreferenceProvider>
  );
}
